import React from 'react';
import { Link } from 'react-router-dom';

function PrivacyPolicy() {
    return (
        <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/40 to-teal-50/30 pt-24 pb-16 font-sans">
            <div
                className="pointer-events-none absolute inset-0 opacity-[0.04]"
                style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #1e3a5f 1px, transparent 0)', backgroundSize: '32px 32px' }}
            />
            <div className="pointer-events-none absolute top-0 right-0 h-[520px] w-[520px] translate-x-1/3 -translate-y-1/3 rounded-full bg-blue-100/60 blur-3xl" />
            <div className="pointer-events-none absolute bottom-0 left-0 h-[420px] w-[420px] -translate-x-1/4 translate-y-1/3 rounded-full bg-teal-100/50 blur-3xl" />

            <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-8">
                <div className="mb-8 rounded-2xl border border-slate-200 bg-white/80 backdrop-blur-sm px-6 py-5 shadow-sm">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h1 className="text-3xl font-bold tracking-tight text-slate-900">Privacy Policy</h1>
                            <p className="mt-2 text-sm text-slate-600">How we collect, use, and protect your data on AmpTrans.</p>
                            <p className="mt-1 text-xs text-slate-500">Last updated October 22, 2026</p>
                        </div>
                        <Link
                            to="/"
                            className="inline-flex items-center justify-center rounded-lg bg-blue-700 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:bg-blue-800"
                        >
                            Back to Home
                        </Link>
                    </div>
                </div>

                <div className="space-y-8 rounded-2xl border border-slate-200 bg-white/90 p-8 shadow-xl backdrop-blur-sm [&_h2]:text-slate-900 [&_h2]:text-2xl [&_h2]:font-semibold [&_h3]:text-slate-800 [&_h3]:text-xl [&_h3]:font-semibold [&_h4]:text-slate-800 [&_h4]:font-semibold [&_h5]:text-slate-700 [&_h5]:font-semibold [&_li]:text-slate-600 [&_p]:text-slate-600 [&_strong]:text-slate-800 [&_ul]:marker:text-slate-400">
                    <section>
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Information We Collect</h2>
                        <p className="text-gray-700 mb-4">
                            We collect information you provide directly to us, such as when you create an account, 
                            use our services, or contact us for support.
                        </p>
                        <ul className="list-disc list-inside text-gray-700 space-y-2">
                            <li>Account information (name, email address, password)</li>
                            <li>Usage data and analytics</li>
                            <li>Communication preferences</li>
                            <li>Technical information about your device and browser</li>
                            <li>Approximate sign-in area (country, region, and city, rounded to about 10 kilometers) derived from the network address used when you sign in. We keep that area so we can see where accounts sign in. We do not store the network address, and we do not attach the area to your account.</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">How We Use Your Information</h2>
                        <p className="text-gray-700 mb-4">
                            We use the information we collect to provide, maintain, and improve our services:
                        </p>
                        <ul className="list-disc list-inside text-gray-700 space-y-2">
                            <li>Provide and maintain our services</li>
                            <li>Process transactions and send related information</li>
                            <li>Send technical notices and support messages</li>
                            <li>Respond to your comments and questions</li>
                            <li>Improve our services and develop new features</li>
                            <li>See the general areas where people sign in</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Cookies and Tracking Technologies</h2>
                        <p className="text-gray-700 mb-4">
                            We use cookies and similar tracking technologies to collect and use personal information about you. 
                            Cookies are small text files that are placed on your computer or mobile device when you visit a website. 
                            They are widely used to make websites work more efficiently and to provide information to website owners.
                        </p>
                        
                        <h3 className="text-xl font-semibold text-gray-900 mb-4">How We Use Cookies</h3>
                        <p className="text-gray-700 mb-4">
                            We use cookies to enhance your experience on our website, analyze site usage, and personalize content. 
                            We categorize our cookies into four main types:
                        </p>
                        
                        <h3 className="text-xl font-semibold text-gray-900 mb-4">Types of Cookies We Use</h3>
                        <div className="space-y-6">
                            <div className="border border-gray-200 rounded-lg p-6">
                                <div className="flex items-center mb-3">
                                    <div className="w-4 h-4 bg-green-500 rounded-full mr-3"></div>
                                    <h4 className="text-xl font-semibold text-gray-900">Necessary Cookies</h4>
                                    <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                        Always Active
                                    </span>
                                </div>
                                <p className="text-gray-700 mb-3">
                                    These cookies are essential for the website to function properly. They enable basic functions like 
                                    page navigation, access to secure areas, and remembering your login status.
                                </p>
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h5 className="font-semibold text-gray-900 mb-2">Examples:</h5>
                                    <ul className="text-sm text-gray-700 space-y-1">
                                        <li>• Authentication and security cookies</li>
                                        <li>• Session management cookies</li>
                                        <li>• Load balancing cookies</li>
                                        <li>• User interface customization cookies</li>
                                    </ul>
                                </div>
                            </div>

                            <div className="border border-gray-200 rounded-lg p-6">
                                <div className="flex items-center mb-3">
                                    <div className="w-4 h-4 bg-blue-500 rounded-full mr-3"></div>
                                    <h4 className="text-xl font-semibold text-gray-900">Analytics Cookies</h4>
                                    <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        Optional
                                    </span>
                                </div>
                                <p className="text-gray-700 mb-3">
                                    These cookies help us understand how visitors interact with our website by collecting and 
                                    reporting information anonymously. This helps us improve our website's performance and user experience.
                                </p>
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h5 className="font-semibold text-gray-900 mb-2">Examples:</h5>
                                    <ul className="text-sm text-gray-700 space-y-1">
                                        <li>• Google Analytics cookies (_ga, _gid, _gat)</li>
                                        <li>• Page view tracking</li>
                                        <li>• User behavior analysis</li>
                                        <li>• Performance monitoring</li>
                                    </ul>
                                </div>
                            </div>

                            <div className="border border-gray-200 rounded-lg p-6">
                                <div className="flex items-center mb-3">
                                    <div className="w-4 h-4 bg-yellow-500 rounded-full mr-3"></div>
                                    <h4 className="text-xl font-semibold text-gray-900">Functional Cookies</h4>
                                    <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                        Optional
                                    </span>
                                </div>
                                <p className="text-gray-700 mb-3">
                                    These cookies enable enhanced functionality and personalization, such as remembering your 
                                    preferences and settings to provide a more personalized experience.
                                </p>
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h5 className="font-semibold text-gray-900 mb-2">Examples:</h5>
                                    <ul className="text-sm text-gray-700 space-y-1">
                                        <li>• Language preference cookies</li>
                                        <li>• Theme and display settings</li>
                                        <li>• User interface preferences</li>
                                        <li>• Accessibility settings</li>
                                    </ul>
                                </div>
                            </div>

                            <div className="border border-gray-200 rounded-lg p-6">
                                <div className="flex items-center mb-3">
                                    <div className="w-4 h-4 bg-purple-500 rounded-full mr-3"></div>
                                    <h4 className="text-xl font-semibold text-gray-900">Marketing Cookies</h4>
                                    <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                        Optional
                                    </span>
                                </div>
                                <p className="text-gray-700 mb-3">
                                    These cookies are used to track visitors across websites to display relevant and engaging 
                                    advertisements and measure the effectiveness of advertising campaigns.
                                </p>
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h5 className="font-semibold text-gray-900 mb-2">Examples:</h5>
                                    <ul className="text-sm text-gray-700 space-y-1">
                                        <li>• Facebook Pixel cookies (_fbp, _fbc)</li>
                                        <li>• Google Ads cookies</li>
                                        <li>• Campaign tracking cookies</li>
                                        <li>• Social media integration cookies</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        
                        <h3 className="text-xl font-semibold text-gray-900 mb-4">Managing Your Cookie Preferences</h3>
                        <p className="text-gray-700 mb-4">
                            You can control and manage cookies in several ways:
                        </p>
                        <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                            <li>Use our cookie settings panel to customize your preferences</li>
                            <li>Configure your browser settings to block or delete cookies</li>
                            <li>Use browser extensions to manage cookies</li>
                            <li>Opt out of specific tracking services</li>
                        </ul>
                        
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <h4 className="font-semibold text-blue-900 mb-2">Cookie Settings</h4>
                            <p className="text-blue-800 text-sm mb-3">
                                You can change your cookie preferences at any time by clicking the "Cookie Settings" 
                                button in our cookie banner or footer.
                            </p>
                            <button
                                onClick={() => {
                                    // This would trigger the cookie settings modal
                                    const event = new CustomEvent('openCookieSettings');
                                    window.dispatchEvent(event);
                                }}
                                className="inline-flex items-center justify-center rounded-lg bg-blue-700 px-4 py-2 text-sm font-medium text-white shadow-sm transition-all hover:bg-blue-800"
                            >
                                Open Cookie Settings
                            </button>
                        </div>
                        
                        <h3 className="text-xl font-semibold text-gray-900 mb-4">Third-Party Cookies</h3>
                        <p className="text-gray-700 mb-4">
                            Some cookies on our website are set by third-party services that appear on our pages. 
                            We have no control over these cookies and you should check the relevant third-party website 
                            for more information about their cookies and how to manage them.
                        </p>
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <h4 className="font-semibold text-gray-900 mb-2">Third-Party Services We Use:</h4>
                            <ul className="text-sm text-gray-700 space-y-1">
                                <li>• Google Analytics - for website analytics</li>
                                <li>• Google Maps - for location services</li>
                                <li>• Stripe - for payment processing</li>
                                <li>• Social media platforms - for sharing features</li>
                            </ul>
                        </div>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Your Rights and Choices</h2>
                        <p className="text-gray-700 mb-4">
                            You have certain rights regarding your personal information under applicable privacy laws:
                        </p>
                        
                        <h3 className="text-xl font-semibold text-gray-900 mb-3">GDPR Rights (EU Residents)</h3>
                        <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                            <li><strong>Right of Access:</strong> Request information about the personal data we process about you</li>
                            <li><strong>Right to Rectification:</strong> Correct inaccurate or incomplete personal data</li>
                            <li><strong>Right to Erasure:</strong> Request deletion of your personal data ("right to be forgotten")</li>
                            <li><strong>Right to Data Portability:</strong> Receive your data in a structured, machine-readable format</li>
                            <li><strong>Right to Object:</strong> Object to processing of your personal data for direct marketing or legitimate interests</li>
                            <li><strong>Right to Restrict Processing:</strong> Request restriction of processing in certain circumstances</li>
                        </ul>
                        
                        <h3 className="text-xl font-semibold text-gray-900 mb-3">CCPA Rights (California Residents)</h3>
                        <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                            <li><strong>Right to Know:</strong> Request information about the personal information we collect, use, and share</li>
                            <li><strong>Right to Delete:</strong> Request deletion of your personal information</li>
                            <li><strong>Right to Opt-Out:</strong> Opt out of the sale of your personal information</li>
                            <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your privacy rights</li>
                        </ul>
                        
                        <h3 className="text-xl font-semibold text-gray-900 mb-3">General Rights</h3>
                        <ul className="list-disc list-inside text-gray-700 space-y-2">
                            <li>Access and update your personal information</li>
                            <li>Delete your account and associated data</li>
                            <li>Opt out of marketing communications</li>
                            <li>Control cookie preferences through our cookie settings</li>
                            <li>Request a copy of your data</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Legal Basis for Processing (GDPR)</h2>
                        <p className="text-gray-700 mb-4">
                            Under GDPR, we process your personal data based on the following legal grounds:
                        </p>
                        <ul className="list-disc list-inside text-gray-700 space-y-2">
                            <li><strong>Consent:</strong> When you have given clear consent for processing (e.g., marketing communications, analytics cookies)</li>
                            <li><strong>Contract Performance:</strong> When processing is necessary for the performance of a contract with you</li>
                            <li><strong>Legitimate Interests:</strong> When we have legitimate business interests that don't override your privacy rights</li>
                            <li><strong>Legal Obligation:</strong> When processing is necessary to comply with legal obligations</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Retention</h2>
                        <p className="text-gray-700 mb-4">
                            We retain your personal information only for as long as necessary to fulfill the purposes outlined in this policy:
                        </p>
                        <ul className="list-disc list-inside text-gray-700 space-y-2">
                            <li><strong>Account Data:</strong> Retained for the duration of your account plus 3 years for legal and business purposes</li>
                            <li><strong>Analytics Data:</strong> Retained for 26 months (Google Analytics default) or until consent is withdrawn</li>
                            <li><strong>Marketing Data:</strong> Retained until you opt out or for 3 years of inactivity</li>
                            <li><strong>Legal Compliance:</strong> Some data may be retained longer to comply with legal obligations</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">International Data Transfers</h2>
                        <p className="text-gray-700 mb-4">
                            Your personal information may be transferred to and processed in countries other than your own. 
                            When we transfer data internationally, we ensure appropriate safeguards are in place:
                        </p>
                        <ul className="list-disc list-inside text-gray-700 space-y-2">
                            <li>Standard Contractual Clauses (SCCs) for EU data transfers</li>
                            <li>Adequacy decisions where applicable</li>
                            <li>Other appropriate safeguards as required by applicable law</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Security</h2>
                        <p className="text-gray-700 mb-4">
                            We implement appropriate technical and organizational measures to protect your personal information 
                            against unauthorized access, alteration, disclosure, or destruction:
                        </p>
                        <ul className="list-disc list-inside text-gray-700 space-y-2">
                            <li>Encryption of data in transit and at rest</li>
                            <li>Regular security assessments and updates</li>
                            <li>Access controls and authentication measures</li>
                            <li>Staff training on data protection</li>
                            <li>Incident response procedures</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Breach Notification</h2>
                        <p className="text-gray-700 mb-4">
                            In the event of a data breach that may result in a high risk to your rights and freedoms, 
                            we will notify you and the relevant supervisory authority within 72 hours of becoming aware of the breach.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Changes to This Policy</h2>
                        <p className="text-gray-700 mb-4">
                            We may update this privacy policy from time to time. We will notify you of any changes by posting 
                            the new privacy policy on this page and updating the "Last Updated" date.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Us</h2>
                        <p className="text-gray-700 mb-4">
                            If you have any questions about this privacy policy or our privacy practices, please contact us at:
                        </p>
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <p className="text-gray-700">
                                Email: info@amptrans.net<br />
                            </p>
                        </div>
                    </section>

                    <div className="border-t border-slate-200 pt-4 text-sm text-slate-500">
                        <p>Last Updated: {new Date().toLocaleDateString()}</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PrivacyPolicy;